import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from './Sidebar';
import DepartmentImage from '../../../assets/image/department/team-doctors-meeting.jpg';
import DepartmentImage2 from '../../../assets/image/department/buildings.jpg';

function HealthHumanServices() {

  const [Faq, setFaq] = useState(0);

  // pick up theme colors from existing DOM
  const titleRef = useRef(null);
  const bodyRef = useRef(null);
  const [titleColor, setTitleColor] = useState('');
  const [bodyColor, setBodyColor] = useState('');

  useEffect(() => {
    if (titleRef.current) setTitleColor(window.getComputedStyle(titleRef.current).color);
    if (bodyRef.current) setBodyColor(window.getComputedStyle(bodyRef.current).color);
  }, []);

  const overviewEmphasis = { color: titleColor || '#0f172a' };

  // compact stacked subtitle/description layout to avoid awkward wraps
  const liRow = { display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px' };
  const icon = { marginTop: '4px', flex: '0 0 auto' };
  const kv = { display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: 0 };
  const keyText = { fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.35, color: titleColor || '#0f172a' };
  const valText = { fontSize: '0.95rem', lineHeight: 1.6, color: bodyColor || undefined };

  const Drop = [
    {
      Que: "What health programs are available for low-income families?",
      Answer:
        "We connect eligible residents to Medicaid/CHIP, community health clinics, preventive screenings, immunizations, WIC and SNAP nutrition support, and case management for chronic conditions. Families can apply online, by phone, or in person at partner sites with multilingual assistance."
    },
    {
      Que: "How can I access mental health services?",
      Answer:
        "Use our mental health navigation line or visit a local clinic. We offer counseling, crisis stabilization, medication management, substance-use treatment, and referrals to community-based supports. Telehealth is available where appropriate."
    },
    {
      Que: "What initiatives support public health and wellness?",
      Answer:
        "Countywide vaccination campaigns, maternal and child health programs, harm reduction, tobacco cessation, chronic disease prevention (diabetes, hypertension), environmental health inspections, and emergency preparedness training."
    },
    {
      Que: "Who is eligible and what documents are needed?",
      Answer:
        "Eligibility varies by program. Typical documents include proof of identity, residency, income, and household size. Immigration status rules differ by service; many public health services are available regardless of status."
    },
    {
      Que: "How much do services cost?",
      Answer:
        "Many services are free or low-cost based on income. For programs with copays or fees, we provide sliding-scale options and help residents apply for financial assistance."
    },
    {
      Que: "Is language or disability access available?",
      Answer:
        "Yes. We provide interpreter services, translated materials, accessible formats, and reasonable accommodations at clinics and service centers."
    },
    {
      Que: "How do I report a public health concern?",
      Answer:
        "Call the public health hotline or submit a report online for issues like food safety, vector control, hazardous spills, or communicable disease concerns."
    }
  ];

  return (
    <>
      <section className="department-details-section">
        <div className="container">
          <div className="row">

            {/* Sidebar */}
            <div className="col-12 col-lg-4 col-xl-4">
              <Sidebar />
            </div>

            {/* Main Content */}
            <div className="col-lg-8">
              <div className="department-details-imgbox">
                <img src={DepartmentImage} className="img-fluid" alt="Health and Human Services" />
              </div>

              <div className="department-details-content-box">
                <h4 ref={titleRef} className="department-details-title">Health and Human Services</h4>
                <p style={overviewEmphasis}>
                  We promote public health, expand access to care, and deliver essential human services for vulnerable residents.
                  Our priorities: prevention first, equitable access, and coordinated care across community partners.
                </p>
                <p>
                  Core functions include primary and behavioral health access, maternal and child health, disease prevention and
                  control, nutrition support, aging and disability services, housing stabilization resources, and emergency
                  preparedness. We work with hospitals, FQHCs, schools, faith-based organizations, and nonprofits to reach people
                  where they are.
                </p>
              </div>

              <div className="department-details-box">
                <div className="department-details-policy">
                  <span>
                    Dedicated to safeguarding the health and well-being of all citizens.
                  </span>
                </div>
                {/* Reference paragraph to capture normal/body color */}
                <p ref={bodyRef}>
                  Our programs address healthcare access, disease prevention, and social services to improve quality of life and
                  community outcomes. We use data-driven approaches, culturally responsive care, and multilingual outreach to close
                  gaps and reduce preventable illness.
                </p>
              </div>

              <div className="department-details-benefits-inner-box">
                <div className="row row-gutter-30">
                  <div className="col-lg-6">
                    <div className="department-details-benefits-box-image">
                      <img src={DepartmentImage2} className="img-fluid" alt="Public Health Services" />
                    </div>
                  </div>

                  <div className="col-lg-6">
                    <div className="department-details-benefits-box">
                      <h3>Our Benefits</h3>
                      <p>
                        Enhancing health and human services for a stronger community.
                      </p>
                      <ul className="list-unstyled list-style" style={{ marginTop: '-15px' }}>
                        <li>
                          <Link to="/department/health/initiatives">
                            <i className="fa-solid fa-circle-arrow-right" style={{ marginRight: '5px' }}/>
                            Public health initiatives
                          </Link>
                        </li>
                        <li>
                          <Link to="/department/health/mental-health-services">
                            <i className="fa-solid fa-circle-arrow-right" style={{ marginRight: '5px' }}/>
                            Mental Health and Substance Abuse Services
                          </Link>
                        </li>
                        <li>
                          <Link to="/department/health/family-community-health">
                            <i className="fa-solid fa-circle-arrow-right" style={{ marginRight: '5px' }}/>
                            Family and Community Health Programs
                          </Link>
                        </li>
                        <li>
                          <Link to="/department/health/emergency-health-response">
                            <i className="fa-solid fa-circle-arrow-right" style={{ marginRight: '5px' }}/>
                            Emergency Health Response and Preparedness
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* NEW section between Benefits and What We Do */}
              <div className="department-details-content-box" style={{ marginTop: '10px' }}>
                <h4
                  className="department-details-title"
                  style={{ color: titleColor || '#0f172a', marginBottom: '6px' }}
                >
                  Outcomes we aim for
                </h4>
                <p style={{ color: bodyColor || undefined, fontSize: '0.95rem' }}>
                  Higher immunization coverage, fewer avoidable ER visits, earlier detection and management of chronic disease,
                  consistent nutrition for children and older adults, and faster community recovery following public-health emergencies.
                </p>
              </div>

              {/* What We Do — compact stacked layout */}
              <div className="department-details-benefits-box">
                <h3>What We Do</h3>
                <ul className="list-unstyled list-style">
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon} />
                    <div style={kv}>
                      <div style={keyText}>Access to Care & Coverage Navigation</div>
                      <div style={valText}>
                        Eligibility screening, enrollment assistance (Medicaid/CHIP), and referrals to primary, dental, and behavioral health.
                      </div>
                    </div>
                  </li>
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon} />
                    <div style={kv}>
                      <div style={keyText}>Prevention & Community Health</div>
                      <div style={valText}>
                        Vaccinations, screenings, health education, tobacco cessation, harm reduction, and injury prevention.
                      </div>
                    </div>
                  </li>
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon} />
                    <div style={kv}>
                      <div style={keyText}>Social Services & Stabilization</div>
                      <div style={valText}>
                        Nutrition programs (WIC/SNAP outreach), aging and disability supports, housing and family assistance coordination.
                      </div>
                    </div>
                  </li>
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon} />
                    <div style={kv}>
                      <div style={keyText}>Emergency Preparedness</div>
                      <div style={valText}>
                        Public health emergency planning, community training, and rapid response with local, state, and federal partners.
                      </div>
                    </div>
                  </li>
                </ul>
              </div>

              {/* Who We Serve */}
              <div className="department-details-benefits-box">
                <h3>Who We Serve</h3>
                <ul className="list-unstyled list-style">
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon}/>
                    <div style={kv}>
                      <div style={keyText}>Families & Children</div>
                      <div style={valText}>Well-child care, immunizations, maternal and child health, and early interventions.</div>
                    </div>
                  </li>
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon}/>
                    <div style={kv}>
                      <div style={keyText}>Older Adults & Caregivers</div>
                      <div style={valText}>Nutrition, fall prevention, care navigation, and in-home supports to age safely.</div>
                    </div>
                  </li>
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon}/>
                    <div style={kv}>
                      <div style={keyText}>People with Disabilities</div>
                      <div style={valText}>Accessibility, assistive technology referrals, ADA accommodations, and benefits counseling.</div>
                    </div>
                  </li>
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon}/>
                    <div style={kv}>
                      <div style={keyText}>Immigrants & Refugees</div>
                      <div style={valText}>Culturally and linguistically appropriate services, screenings, and linkage to care.</div>
                    </div>
                  </li>
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon}/>
                    <div style={kv}>
                      <div style={keyText}>People Experiencing Homelessness</div>
                      <div style={valText}>Mobile outreach, hygiene support, case management, and connections to housing services.</div>
                    </div>
                  </li>
                </ul>
              </div>

              {/* Provider & Community Partnerships */}
              <div className="department-details-benefits-box">
                <h3>Provider & Community Partnerships</h3>
                <ul className="list-unstyled list-style">
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon}/>
                    <div style={kv}>
                      <div style={keyText}>Clinics & FQHCs</div>
                      <div style={valText}>Care coordination, immunization campaigns, and chronic disease management protocols.</div>
                    </div>
                  </li>
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon}/>
                    <div style={kv}>
                      <div style={keyText}>Hospitals & EMS</div>
                      <div style={valText}>Syndromic surveillance, discharge linkage to services, and emergency preparedness drills.</div>
                    </div>
                  </li>
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon}/>
                    <div style={kv}>
                      <div style={keyText}>Schools & Universities</div>
                      <div style={valText}>School-based health education, screenings, and internship pipelines for public health careers.</div>
                    </div>
                  </li>
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon}/>
                    <div style={kv}>
                      <div style={keyText}>Faith & Community Orgs</div>
                      <div style={valText}>Trusted outreach, pop-up clinics, and resource navigation in neighborhoods.</div>
                    </div>
                  </li>
                </ul>
              </div>

              {/* Quality & Safety */}
              <div className="department-details-benefits-box">
                <h3>Quality & Safety</h3>
                <ul className="list-unstyled list-style">
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon}/>
                    <div style={kv}>
                      <div style={keyText}>Data & Reporting</div>
                      <div style={valText}>Outcomes tracking, dashboards, and continuous quality improvement cycles.</div>
                    </div>
                  </li>
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon}/>
                    <div style={kv}>
                      <div style={keyText}>Cultural & Linguistic Access</div>
                      <div style={valText}>Interpreter services, translated materials, and staff training on equity and inclusion.</div>
                    </div>
                  </li>
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon}/>
                    <div style={kv}>
                      <div style={keyText}>Privacy & Security</div>
                      <div style={valText}>HIPAA compliance, secure records, and confidentiality protections across programs.</div>
                    </div>
                  </li>
                </ul>
              </div>

              {/* How to Get Care */}
              <div className="department-details-content-box">
                <h4 className="department-details-title" style={{ color: titleColor || '#0f172a', marginBottom: '6px' }}>
                  How to Get Care
                </h4>
                <ol style={{ paddingLeft: '18px', color: bodyColor || undefined, fontSize: '0.95rem', lineHeight: 1.6 }}>
                  <li>Call the hotline or use the online portal to start an intake.</li>
                  <li>Have ID and basic household info ready; interpreter services available.</li>
                  <li>Get matched to clinics, benefits, or case management based on need.</li>
                  <li>Schedule services and receive reminders and follow-up support.</li>
                </ol>
              </div>

              <div className="accordian-box">
                {Drop.map((items, index) => (
                  <div className={`accordian-box-item ${Faq !== index && "active"}`} key={index}>
                    <div className="accordian-title">
                      <h5>{items.Que}</h5>
                      <i className="fa-solid fa-angle-right" onClick={() => setFaq(index)} />
                    </div>

                    {Faq === index && (
                      <div className="accordian-content" style={{ display: "block" }}>
                        <p>{items.Answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>
    </>
  );
}

export default HealthHumanServices;
